import { REGION_BY_ID } from '@/pages/demo/revenue/components/revenueData';
import { LEAD_STAGES, leadValueLabel, formatDate, regionLabels } from './sponsorData';
import type { Lead, LeadStage } from './sponsorData';

type Props = {
  leads: Lead[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  onSetStage: (id: string, stage: LeadStage) => void;
};

const DOT: Record<LeadStage, string> = {
  'New inquiry': 'bg-accent-500',
  Qualified: 'bg-secondary-500',
  'Proposal sent': 'bg-foreground-400',
  Negotiation: 'bg-accent-600',
  Won: 'bg-primary-500',
  Lost: 'bg-background-400',
};

export default function LeadBoard({ leads, selectedId, onSelect, onSetStage }: Props) {
  const indexOf = (s: LeadStage) => LEAD_STAGES.indexOf(s);

  return (
    <div className="flex gap-4 overflow-x-auto pb-2 -mx-1 px-1">
      {LEAD_STAGES.map((stage) => {
        const col = leads.filter((l) => l.stage === stage);
        const total = col.reduce((s, l) => s + (l.valueMonthly ?? 0), 0);
        return (
          <div key={stage} className="w-[260px] shrink-0 flex flex-col">
            <div className="flex items-center justify-between gap-2 px-1 pb-3">
              <div className="flex items-center gap-2 min-w-0">
                <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${DOT[stage]}`}></span>
                <p className="font-label font-bold uppercase tracking-wide text-[11px] text-foreground-800 truncate">{stage}</p>
              </div>
              <span className="font-label font-bold text-[11px] text-foreground-500 bg-background-100 border border-background-200 rounded-full px-2 py-0.5 shrink-0">{col.length}</span>
            </div>
            <p className="px-1 pb-2 text-[11px] text-foreground-500">{total > 0 ? `$${total.toLocaleString('en-US')}/mo` : '—'}</p>

            <div className="space-y-3 flex-1 bg-background-100/60 border border-background-200 rounded-lg p-2.5 min-h-[120px]">
              {col.map((lead) => {
                const i = indexOf(lead.stage);
                const on = lead.id === selectedId;
                return (
                  <div
                    key={lead.id}
                    className={`bg-background-50 border rounded-md p-3 cursor-pointer transition-colors ${on ? 'border-primary-500 ring-1 ring-primary-400' : 'border-background-200 hover:border-primary-300'}`}
                    onClick={() => onSelect(lead.id)}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <p className="font-heading font-bold text-sm text-foreground-950 leading-tight">{lead.company}</p>
                      <span className="font-label font-bold text-[10px] text-foreground-600 bg-background-100 border border-background-200 rounded-sm px-1.5 py-0.5 whitespace-nowrap">{leadValueLabel(lead)}</span>
                    </div>
                    <p className="mt-1 text-xs text-foreground-600 truncate">{lead.contact}</p>
                    <p className="mt-1.5 text-[11px] text-foreground-500 truncate">{regionLabels(lead.regionIds, REGION_BY_ID)}</p>
                    <div className="mt-2.5 flex items-center justify-between gap-2">
                      <span className="inline-flex items-center gap-1 text-[10px] text-foreground-500 font-label uppercase tracking-wide">
                        <span className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-user-star-line"></i></span>
                        {lead.owner}
                      </span>
                      {lead.nextStep && (
                        <span className="text-[10px] text-foreground-500 whitespace-nowrap">{formatDate(lead.nextStep)}</span>
                      )}
                    </div>
                    {/* Move controls */}
                    <div className="mt-2.5 flex items-center gap-1 border-t border-background-200 pt-2">
                      <button
                        type="button"
                        disabled={i === 0}
                        onClick={(e) => { e.stopPropagation(); onSetStage(lead.id, LEAD_STAGES[i - 1]); }}
                        className="w-6 h-6 flex items-center justify-center rounded-md border border-background-200 text-foreground-600 hover:border-primary-400 hover:text-primary-600 disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer"
                        aria-label="Move to previous stage"
                      >
                        <i className="ri-arrow-left-s-line"></i>
                      </button>
                      <button
                        type="button"
                        disabled={i === LEAD_STAGES.length - 1}
                        onClick={(e) => { e.stopPropagation(); onSetStage(lead.id, LEAD_STAGES[i + 1]); }}
                        className="w-6 h-6 flex items-center justify-center rounded-md border border-background-200 text-foreground-600 hover:border-primary-400 hover:text-primary-600 disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer"
                        aria-label="Move to next stage"
                      >
                        <i className="ri-arrow-right-s-line"></i>
                      </button>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); onSelect(lead.id); }}
                        className="ml-auto font-label uppercase tracking-wide text-[10px] font-semibold text-primary-600 hover:text-primary-700 cursor-pointer whitespace-nowrap"
                      >
                        Open
                      </button>
                    </div>
                  </div>
                );
              })}
              {col.length === 0 && <p className="text-xs text-foreground-400 px-1 py-3 text-center">Empty</p>}
            </div>
          </div>
        );
      })}
    </div>
  );
}